import * as THREE from 'three';
import * as DT from '@/engine/PlanningAlgorithm/DataTypes';
import type { ITypedValueJsonIO } from '@/engine/DataFlow/DataFlow';
import type { IValue } from '@/engine/DataFlow/Graph';
import type { Type } from '@/engine/DataFlow/Type';
import { DistanceModel } from '@/engine/app/modules/indications/distance/Distance.model';
import { AngleModel } from '@/engine/app/modules/indications/angle/Angle.model';
import { UniqueEntityID } from '../core/uniqueEntityId';
import { AnnotationModel } from '../app/modules/indications/annotation/Annotation.model';
import { centroid, unitNormal } from '../PlanningAlgorithm/Helpers';

export class DataFlowJsonIO implements ITypedValueJsonIO {
  public export(type: Type, value: IValue | undefined): unknown {
    if (value === undefined) {
      return undefined;
    }
    if (type == DT.StringT.Type) {
      return (value as DT.StringT).value;
    } else if (type == DT.NumberT.Type) {
      return (value as DT.NumberT).value;
    } else if (type == DT.BooleanT.Type) {
      return (value as DT.BooleanT).value;
    } else if (type == DT.TuneFlag.Type) {
      return (value as DT.TuneFlag).value;
    } else if (type == DT.RangeT.Type) {
      const val = (value as DT.RangeT).value;
      return { from: val[0], to: val[1] };
    } else if (type == DT.Vector.Type) {
      return (value as DT.Vector).value.toArray();
    } else if (type == DT.Point.Type) {
      return (value as DT.Point).value.toArray();
    } else if (type == DT.ArrayT.Type) {
      return (value as DT.ArrayT).value;
    } else if (type == DT.Plane.Type) {
      const val = (value as DT.Plane).value;
      return {
        origin: val.origin.toArray(),
        normal: val.planeEq.normal.toArray()
      };
    } else if (type == DT.Line.Type) {
      const val = (value as DT.Line).value;
      return { from: val.start.toArray(), to: val.end.toArray() };
    } else if (type == DT.Polyline.Type) {
      const val = (value as DT.Polyline).value;
      return {
        isClosed: val.isClosed,
        points: val.points.map((v) => v.toArray())
      };
    } else if (type == DT.Orientation.Type) {
      const val = (value as DT.Orientation).value;
      return { top: val.top.toArray(), dir: val.dir.toArray() };
    } else if (type == DistanceModel.Type) {
      const val = (value as DistanceModel).props;
      return {
        from: val.from?.toArray(),
        to: val.to?.toArray()
      };
    } else if (type == AngleModel.Type) {
      const val = (value as AngleModel).props;
      return {
        from: val.from?.toArray(),
        corner: val.corner?.toArray(),
        to: val.to?.toArray()
      };
    } else if (type == AnnotationModel.Type) {
      const val = (value as AnnotationModel).props;
      return {
        point: val.point?.toArray(),
        labelPoint: val.labelPoint?.toArray(),
        text: val.text
      };
    } else if (type == DT.PolylinesVector.Type) {
      const val = (value as DT.PolylinesVector).value;
      return {
        polylines: val.polylines.map((p) => {
          return { isClosed: p.value.isClosed, points: p.value.points.map((v) => v.toArray()) };
        })
      };
    } else if (type == DT.IdentifiersList.Type) {
      return (value as DT.IdentifiersList).value;
    } else if (type == DT.AreaMeasurement.Type) {
      const val = value as DT.AreaMeasurement;
      return {
        basePts: val.basePts.map((v) => v.toArray()),
        splinePts: val.splinePts.map((v) => v.toArray()),
        area: val.area,
        perimeter: val.perimeter,
        dA: val.dA,
        dP: val.dP,
        center: val.center.toArray(),
        normal: val.normal.toArray(),
        orientationY: val.orientationY?.toArray(),
        minCorner: val.minCorner?.toArray(),
        maxCorner: val.maxCorner?.toArray(),
        lMin: val.lMin ? { from: val.lMin.start.toArray(), to: val.lMin.end.toArray() } : undefined,
        lMax: val.lMax ? { from: val.lMax.start.toArray(), to: val.lMax.end.toArray() } : undefined,
        lPerp: val.lPerp ? { from: val.lPerp.start.toArray(), to: val.lPerp.end.toArray() } : undefined,
        dMean: val.dMean,
        lX: val.lX ? { from: val.lX.start.toArray(), to: val.lX.end.toArray() } : undefined,
        lY: val.lY ? { from: val.lY.start.toArray(), to: val.lY.end.toArray() } : undefined
      };
    } else if (type == DT.SplineMeasurement.Type) {
      const val = value as DT.SplineMeasurement;
      return {
        splinePts: val.splinePts.map((v) => v.toArray()),
        isClosed: val.isClosed,
        length: val.length,
        center: val.center,
        normal: val.normal
      };
    } else {
      throw new Error('No JSON exporter for type: [' + type.name + ']');
    }
  }

  public import(json: unknown, type: string, vertexName?: string): IValue | undefined {
    if (json === undefined) {
      return undefined;
    } else if (type == DT.StringT.Type.name) {
      return new DT.StringT(json as string);
    } else if (type == DT.NumberT.Type.name) {
      return new DT.NumberT(json as number);
    } else if (type == DT.BooleanT.Type.name) {
      return new DT.BooleanT(json as boolean);
    } else if (type == DT.TuneFlag.Type.name) {
      return new DT.TuneFlag(json as DT.ETuneFlag);
    } else if (type == DT.RangeT.Type.name) {
      const obj = json as { from: number; to: number };
      return new DT.RangeT([obj.from, obj.to]);
    } else if (type == DT.Vector.Type.name) {
      return new DT.Vector(new THREE.Vector3().fromArray(json as Array<number>));
    } else if (type == DT.Point.Type.name) {
      return new DT.Point(new THREE.Vector3().fromArray(json as Array<number>));
    } else if (type == DT.ArrayT.Type.name) {
      return new DT.ArrayT(json as Array<number>);
    } else if (type == DT.Plane.Type.name) {
      const obj = json as { origin: Array<number>; normal: Array<number> };
      const origin = new THREE.Vector3().fromArray(obj.origin);
      const normal = new THREE.Vector3().fromArray(obj.normal);
      const plane = new THREE.Plane().setFromNormalAndCoplanarPoint(normal, origin);
      return new DT.Plane({ origin: origin, planeEq: plane });
    } else if (type == DT.Line.Type.name) {
      const obj = json as { from: Array<number>; to: Array<number> };
      const from = new THREE.Vector3().fromArray(obj.from);
      const to = new THREE.Vector3().fromArray(obj.to);
      return new DT.Line(new THREE.Line3(from, to));
    } else if (type == DT.Polyline.Type.name) {
      const obj = json as { isClosed: boolean; points: Array<Array<number>> };
      const points = obj.points.map((pt: Array<number>) => new THREE.Vector3().fromArray(pt));
      return new DT.Polyline({ isClosed: obj.isClosed, points: points });
    } else if (type == DT.Orientation.Type.name) {
      const obj = json as { top: Array<number>; dir: Array<number> };
      return new DT.Orientation({
        top: new THREE.Vector3().fromArray(obj.top),
        dir: new THREE.Vector3().fromArray(obj.dir)
      });
    } else if (type == DistanceModel.Type.name) {
      const obj = json as { from?: Array<number>; to?: Array<number> };
      const meas = new DistanceModel({}, new UniqueEntityID(vertexName));
      meas.setFromPoint(obj.from ? new THREE.Vector3().fromArray(obj.from) : undefined);
      meas.setToPoint(obj.to ? new THREE.Vector3().fromArray(obj.to) : undefined);
      return meas;
    } else if (type == AngleModel.Type.name) {
      const obj = json as {
        from?: Array<number>;
        to?: Array<number>;
        corner?: Array<number>;
      };
      const meas = new AngleModel({}, new UniqueEntityID(vertexName));
      meas.setFromPoint(obj.from ? new THREE.Vector3().fromArray(obj.from) : undefined);
      meas.setCornerPoint(obj.corner ? new THREE.Vector3().fromArray(obj.corner) : undefined);
      meas.setToPoint(obj.to ? new THREE.Vector3().fromArray(obj.to) : undefined);
      return meas;
    } else if (type == AnnotationModel.Type.name) {
      const obj = json as {
        point?: Array<number>;
        labelPoint?: Array<number>;
        text?: string;
      };
      const anot = new AnnotationModel({}, new UniqueEntityID(vertexName));
      if (obj.point) anot.setPoint(new THREE.Vector3().fromArray(obj.point));
      if (obj.labelPoint) anot.setLabelPoint(new THREE.Vector3().fromArray(obj.labelPoint));
      if (obj.text) anot.setText(obj.text);
      return anot;
    } else if (type == DT.PolylinesVector.Type.name) {
      const obj = json as {
        polylines: Array<{ isClosed: boolean; points: Array<Array<number>> }>;
      };
      return new DT.PolylinesVector({
        polylines: obj.polylines.map((p) => {
          return new DT.Polyline({
            isClosed: p.isClosed,
            points: p.points.map((pt: Array<number>) => new THREE.Vector3().fromArray(pt))
          });
        })
      });
    } else if (type == DT.IdentifiersList.Type.name) {
      return new DT.IdentifiersList(json as Array<string>);
    } else if (type == DT.AreaMeasurement.Type.name) {
      const obj = json as {
        basePts: Array<Array<number>>;
        splinePts: Array<Array<number>>;
        area: number;
        perimeter: number;
        dA: number;
        dP: number;
        center: Array<number>;
        normal: Array<number>;
        orientationY?: Array<number>;
        minCorner?: Array<number>;
        maxCorner?: Array<number>;
        lMin?: { from: Array<number>; to: Array<number> };
        lMax?: { from: Array<number>; to: Array<number> };
        lPerp?: { from: Array<number>; to: Array<number> };
        dMean?: number;
        lX?: { from: Array<number>; to: Array<number> };
        lY?: { from: Array<number>; to: Array<number> };
      };
      return new DT.AreaMeasurement(
        obj.basePts.map((pt: Array<number>) => new THREE.Vector3().fromArray(pt)),
        obj.splinePts.map((pt: Array<number>) => new THREE.Vector3().fromArray(pt)),
        obj.area,
        obj.perimeter,
        obj.dA,
        obj.dP,
        new THREE.Vector3().fromArray(obj.center),
        new THREE.Vector3().fromArray(obj.normal),
        obj.orientationY ? new THREE.Vector3().fromArray(obj.orientationY) : undefined,
        obj.minCorner ? new THREE.Vector3().fromArray(obj.minCorner) : undefined,
        obj.maxCorner ? new THREE.Vector3().fromArray(obj.maxCorner) : undefined,
        obj.lMin
          ? new THREE.Line3(new THREE.Vector3().fromArray(obj.lMin.from), new THREE.Vector3().fromArray(obj.lMin.to))
          : undefined,
        obj.lMax
          ? new THREE.Line3(new THREE.Vector3().fromArray(obj.lMax.from), new THREE.Vector3().fromArray(obj.lMax.to))
          : undefined,
        obj.lPerp
          ? new THREE.Line3(new THREE.Vector3().fromArray(obj.lPerp.from), new THREE.Vector3().fromArray(obj.lPerp.to))
          : undefined,
        obj.dMean,
        obj.lX
          ? new THREE.Line3(new THREE.Vector3().fromArray(obj.lX.from), new THREE.Vector3().fromArray(obj.lX.to))
          : undefined,
        obj.lY
          ? new THREE.Line3(new THREE.Vector3().fromArray(obj.lY.from), new THREE.Vector3().fromArray(obj.lY.to))
          : undefined
      );
    } else if (type == DT.SplineMeasurement.Type.name) {
      const obj = json as {
        splinePts: Array<Array<number>>;
        isClosed: boolean;
        length: number;
        center?: THREE.Vector3;
        normal?: THREE.Vector3;
      };
      const pts = obj.splinePts.map((pt: Array<number>) => new THREE.Vector3().fromArray(pt));
      const center = obj.center || centroid(pts);
      const normal = obj.normal || unitNormal(center, pts, obj.isClosed);
      return new DT.SplineMeasurement(pts, obj.isClosed, obj.length, center, normal);
    } else {
      throw new Error('No JSON importer for type: [' + type + ']');
    }
  }
}
